/**
 * localAccessService.js - Lokal lagring av modultilgang og passord
 * 
 * VIKTIG: RettVerk lagrer ALLE data lokalt på brukerens maskin.
 * Ingenting lagres på server.
 * 
 * Denne tjenesten håndterer:
 * - Modultilgang per bruker (localStorage)
 * - Passord for ansatte (hashed, localStorage)
 */

// ════════════════════════════════════════════════════════════════
// STORAGE KEYS
// ════════════════════════════════════════════════════════════════

const STORAGE_KEYS = {
  MODULE_ACCESS: 'rv_company_user_module_access',
  USER_PASSWORDS: 'rv_company_user_passwords',
  COMPANY_USERS: 'rv_company_users'
};

// ════════════════════════════════════════════════════════════════
// HELPER FUNCTIONS
// ════════════════════════════════════════════════════════════════

/**
 * Hent data fra localStorage
 */
function getStorageData(key) {
  try {
    const data = localStorage.getItem(key);
    return data ? JSON.parse(data) : {};
  } catch (error) {
    console.error(`Failed to read ${key} from localStorage:`, error);
    return {};
  }
}

/**
 * Lagre data til localStorage
 */
function setStorageData(key, data) {
  try {
    localStorage.setItem(key, JSON.stringify(data));
    return true;
  } catch (error) {
    console.error(`Failed to write ${key} to localStorage:`, error);
    return false;
  }
}

/**
 * Hash passord med SHA-256
 * Bruker Web Crypto API for sikker hashing
 */
async function hashPassword(password) {
  const encoder = new TextEncoder();
  const data = encoder.encode(password);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  return hashHex;
}

/**
 * Verifiser passord mot hash
 */
async function verifyPassword(password, hash) {
  const passwordHash = await hashPassword(password);
  return passwordHash === hash;
}

// ════════════════════════════════════════════════════════════════
// MODULE ACCESS FUNCTIONS
// ════════════════════════════════════════════════════════════════

/**
 * Hent modultilgang for en bruker
 * @param {string} userId - Brukerens ID
 * @returns {Object} - Objekt med moduleKey: boolean
 */
export function getModuleAccess(userId) {
  const allAccess = getStorageData(STORAGE_KEYS.MODULE_ACCESS);
  return allAccess[userId] || {};
}

/**
 * Sett modultilgang for en bruker
 * @param {string} userId - Brukerens ID
 * @param {string} moduleKey - Modulnøkkel (f.eks. "arbeidsrett")
 * @param {boolean} allowed - Om tilgang er gitt
 */
export function setModuleAccess(userId, moduleKey, allowed) {
  const allAccess = getStorageData(STORAGE_KEYS.MODULE_ACCESS);
  
  if (!allAccess[userId]) {
    allAccess[userId] = {};
  }
  
  allAccess[userId][moduleKey] = allowed;
  
  return setStorageData(STORAGE_KEYS.MODULE_ACCESS, allAccess);
}

/**
 * Sett all modultilgang for en bruker
 * @param {string} userId - Brukerens ID
 * @param {Object} moduleAccess - Objekt med moduleKey: boolean
 */
export function setAllModuleAccess(userId, moduleAccess) {
  const allAccess = getStorageData(STORAGE_KEYS.MODULE_ACCESS);
  allAccess[userId] = moduleAccess;
  return setStorageData(STORAGE_KEYS.MODULE_ACCESS, allAccess);
}

/**
 * Hent all modultilgang for hele bedriften
 * @returns {Object} - Objekt med userId: { moduleKey: boolean }
 */
export function getAllAccessForCompany() {
  return getStorageData(STORAGE_KEYS.MODULE_ACCESS);
}

/**
 * Sjekk om bruker har tilgang til en spesifikk modul
 * @param {string} userId - Brukerens ID
 * @param {string} moduleKey - Modulnøkkel
 * @param {boolean} isAdmin - Om brukeren er admin
 * @returns {boolean}
 */
export function hasModuleAccess(userId, moduleKey, isAdmin = false) {
  // Admin har alltid tilgang
  if (isAdmin) return true;
  
  const userAccess = getModuleAccess(userId);
  return userAccess[moduleKey] === true;
}

/**
 * Hent liste over moduler brukeren har tilgang til
 * @param {string} userId - Brukerens ID
 * @param {string[]} companyModules - Liste over bedriftens moduler
 * @param {boolean} isAdmin - Om brukeren er admin
 * @returns {string[]} - Liste over modulnøkler
 */
export function getAllowedModules(userId, companyModules, isAdmin = false) {
  // Admin har tilgang til alle
  if (isAdmin) return companyModules;
  
  const userAccess = getModuleAccess(userId);
  return companyModules.filter(key => userAccess[key] === true);
}

/**
 * Fjern all modultilgang for en bruker
 * @param {string} userId - Brukerens ID
 */
export function removeModuleAccess(userId) {
  const allAccess = getStorageData(STORAGE_KEYS.MODULE_ACCESS);
  delete allAccess[userId];
  return setStorageData(STORAGE_KEYS.MODULE_ACCESS, allAccess);
}

// ════════════════════════════════════════════════════════════════
// PASSWORD FUNCTIONS
// ════════════════════════════════════════════════════════════════

/**
 * Sett passord for en bruker (hashed)
 * @param {string} userId - Brukerens ID
 * @param {string} password - Passordet i klartekst
 * @returns {Promise<boolean>}
 */
export async function setUserPassword(userId, password) {
  try {
    const hashedPassword = await hashPassword(password);
    const allPasswords = getStorageData(STORAGE_KEYS.USER_PASSWORDS);
    allPasswords[userId] = hashedPassword;
    return setStorageData(STORAGE_KEYS.USER_PASSWORDS, allPasswords);
  } catch (error) {
    console.error('Failed to set password:', error);
    return false;
  }
}

/**
 * Verifiser passord for en bruker
 * @param {string} userId - Brukerens ID
 * @param {string} password - Passordet i klartekst
 * @returns {Promise<boolean>}
 */
export async function verifyUserPassword(userId, password) {
  try {
    const allPasswords = getStorageData(STORAGE_KEYS.USER_PASSWORDS);
    const storedHash = allPasswords[userId];
    
    if (!storedHash) return false;
    
    return await verifyPassword(password, storedHash);
  } catch (error) {
    console.error('Failed to verify password:', error);
    return false;
  }
}

/**
 * Sjekk om bruker har passord satt
 * @param {string} userId - Brukerens ID
 * @returns {boolean}
 */
export function hasPasswordSet(userId) {
  const allPasswords = getStorageData(STORAGE_KEYS.USER_PASSWORDS);
  return !!allPasswords[userId];
}

/**
 * Fjern passord for en bruker
 * @param {string} userId - Brukerens ID
 */
export function removeUserPassword(userId) {
  const allPasswords = getStorageData(STORAGE_KEYS.USER_PASSWORDS);
  delete allPasswords[userId];
  return setStorageData(STORAGE_KEYS.USER_PASSWORDS, allPasswords);
}

// ════════════════════════════════════════════════════════════════
// COMPANY USERS FUNCTIONS (lokal cache)
// ════════════════════════════════════════════════════════════════

/**
 * Lagre bruker lokalt
 * @param {Object} user - Brukerobjekt
 */
export function saveLocalUser(user) {
  const allUsers = getStorageData(STORAGE_KEYS.COMPANY_USERS);
  allUsers[user.id] = {
    ...user,
    savedAt: new Date().toISOString()
  };
  return setStorageData(STORAGE_KEYS.COMPANY_USERS, allUsers);
}

/**
 * Hent bruker fra lokal cache
 * @param {string} userId - Brukerens ID
 */
export function getLocalUser(userId) {
  const allUsers = getStorageData(STORAGE_KEYS.COMPANY_USERS);
  return allUsers[userId] || null;
}

/**
 * Hent alle lokale brukere
 */
export function getAllLocalUsers() {
  return getStorageData(STORAGE_KEYS.COMPANY_USERS);
}

/**
 * Fjern bruker fra lokal cache
 * @param {string} userId - Brukerens ID
 */
export function removeLocalUser(userId) {
  const allUsers = getStorageData(STORAGE_KEYS.COMPANY_USERS);
  delete allUsers[userId];
  return setStorageData(STORAGE_KEYS.COMPANY_USERS, allUsers);
}

// ════════════════════════════════════════════════════════════════
// EXPORT DEFAULT OBJECT
// ════════════════════════════════════════════════════════════════

const localAccessService = {
  // Module access
  getModuleAccess,
  setModuleAccess,
  setAllModuleAccess,
  getAllAccessForCompany,
  hasModuleAccess,
  getAllowedModules,
  removeModuleAccess,
  
  // Passwords
  setUserPassword,
  verifyUserPassword,
  hasPasswordSet,
  removeUserPassword,
  
  // Local users
  saveLocalUser,
  getLocalUser,
  getAllLocalUsers,
  removeLocalUser,
  
  // Storage keys (for debugging)
  STORAGE_KEYS
};

export default localAccessService;
